'use client';

import { Button } from '@ui/components/button';
import { AppCard } from '@ui/components/layouts';
import { text } from '@modules/dashboard/text';
import { textBusinessLogic } from '@modules/dashboard/text_business_logic';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCalendarDays } from '@micks-yard/icons/solid/faCalendarDays';
import { faCircle } from '@micks-yard/icons/solid/faCircle';
import { faCircleCheck } from '@micks-yard/icons/solid/faCircleCheck';
import { faSeedling } from '@micks-yard/icons/solid/faSeedling';

import type { SeedWidgetProps } from '@modules/dashboard/containers/seed_widget_container';
import type { SeedRequirement } from '@modules/dashboard/business_logic';
import { PlantAvatarWeb } from '@modules/plants/views/plant_avatar_web';

export function SeedWidgetView({
  selectedGarden,
  seedRequirements,
  onToggleSown,
}: SeedWidgetProps) {
  const unsownSeeds = seedRequirements.filter((req) => !req.hasSown);
  const sownCount = seedRequirements.length - unsownSeeds.length;

  return (
    <AppCard className="p-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <FaIcon icon={faSeedling} className="size-5 text-green-500" />
          <h2 className="text-lg font-semibold">{text.seedsTitle}</h2>
        </div>

        {!selectedGarden ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <FaIcon icon={faCalendarDays} className="text-foreground size-10 opacity-30" />
            <p className="text-muted-foreground text-sm font-medium">{text.selectGardenForSeeds}</p>
          </div>
        ) : (
          <>
            {seedRequirements.length > 0 && (
              <div className="flex items-center gap-2">
                <FaIcon icon={faCircleCheck} className="size-4 text-green-500" />
                <span className="text-muted-foreground text-xs">
                  {text.seedsProgress(sownCount, seedRequirements.length)}
                </span>
              </div>
            )}

            {seedRequirements.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10">
                <FaIcon icon={faSeedling} className="text-foreground size-8 opacity-30" />
                <p className="text-muted-foreground text-sm font-medium">{text.noPlantsInGarden}</p>
                <p className="text-muted-foreground text-xs">{text.addPlantsFirst}</p>
              </div>
            ) : (
              <div className="flex max-h-[600px] flex-col overflow-auto">
                <div className="flex flex-col gap-1">
                  {seedRequirements.map((req) => (
                    <SeedRow
                      key={req.plantId}
                      requirement={req}
                      onToggle={() => onToggleSown(req.plantId, req.hasSown || false)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppCard>
  );
}

interface SeedRowProps {
  requirement: SeedRequirement;
  onToggle: () => void;
}

function SeedRow({ requirement, onToggle }: SeedRowProps) {
  const isSown = requirement.hasSown || false;
  const isOverdue = (requirement.daysUntilSow ?? 0) < 0;

  return (
    <Button
      type="button"
      variant="ghost"
      className={`h-auto w-full justify-start rounded-md px-3 py-2 ${isSown ? 'opacity-60' : ''}`}
      onClick={onToggle}>
      <div className="flex w-full items-center gap-3">
        {isSown ? (
          <FaIcon icon={faCircleCheck} className="size-4 shrink-0 text-green-500" />
        ) : (
          <FaIcon icon={faCircle} className="size-4 shrink-0 text-gray-400" />
        )}
        <PlantAvatarWeb
          name={requirement.plantName}
          emoji={requirement.emoji}
          imagePath={requirement.imagePath}
          size={20}
        />
        <div className="flex flex-1 flex-col items-start gap-0.5">
          <span
            className={`font-medium ${isSown ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
            {requirement.plantName}
          </span>
          <span className={`text-xs ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
            {textBusinessLogic.sowTimingLine(requirement.sowTimingLabel, requirement.daysUntilSow)}
          </span>
        </div>
        <span className={`text-xs ${isSown ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
          {text.seedsCount(requirement.seedsNeeded)}
        </span>
      </div>
    </Button>
  );
}
